/* eslint-disable react/require-default-props -- defaults via destructuring */
import React from 'react';
import PropTypes from 'prop-types';
import { Container, Row, Col, FlexRow } from '../GridsAndLayouts/GridsAndLayouts';
import Typography from '../Typography';
import Button from '../Button';
import './Header.scss';

function cx(...parts) {
  return parts.filter(Boolean).join(' ');
}

/**
 * Page / module header (Figma “Header I–III”). Every block is optional — pass only what you need.
 *
 * Slots: `title` (string or node), `primaryAction`, `search`, `viewSwitcher`, `tabs` (e.g. `<Tabs variant="underline" />`).
 * Layout uses design-system `Container`, `Row`, and `Col`.
 */
export function Header({
  title,
  primaryAction,
  search,
  viewSwitcher,
  tabs,
  className = '',
  showBorderBottom = true,
  ...rest
}) {
  const hasTop = (title != null && title !== false && title !== '') || primaryAction != null;
  const hasToolbar = search != null || viewSwitcher != null;
  const hasTabs = tabs != null;

  if (!hasTop && !hasToolbar && !hasTabs) {
    return null;
  }

  const hasTitle = title != null && title !== false && title !== '';
  const topOnlyActions = primaryAction != null && !hasTitle;
  const titleMd = primaryAction != null && !topOnlyActions ? 8 : 12;
  const actionsMd = primaryAction != null && hasTitle && !topOnlyActions ? 4 : 12;

  return (
    <header className={cx('ds-header', showBorderBottom && 'ds-header--bordered', className)} {...rest}>
      <Container fluid className="ds-header-inner">
        {hasTop ? (
          <Row noGutters className={cx('ds-header-top', topOnlyActions && 'ds-header-top--actions-only')}>
            {hasTitle ? (
              <Col xs={12} md={titleMd} className="ds-header-title-col">
                {typeof title === 'string' ? (
                  <Typography component="h1" variant="heading2XLarge" className="ds-header-title">
                    {title}
                  </Typography>
                ) : (
                  title
                )}
              </Col>
            ) : null}
            {primaryAction != null ? (
              <Col xs={12} md={actionsMd} className="ds-header-actions-col">
                {primaryAction}
              </Col>
            ) : null}
          </Row>
        ) : null}

        {hasToolbar ? (
          <FlexRow
            align="center"
            justify="between"
            className={cx(
              'ds-header-toolbar',
              search != null && viewSwitcher == null && 'ds-header-toolbar--search-only',
              search == null && viewSwitcher != null && 'ds-header-toolbar--switcher-only'
            )}
          >
            {search != null ? <div className="ds-header-toolbar-start">{search}</div> : null}
            {viewSwitcher != null ? <div className="ds-header-toolbar-end">{viewSwitcher}</div> : null}
          </FlexRow>
        ) : null}

        {hasTabs ? (
          <Row noGutters className="ds-header-tabs-row">
            <Col xs={12} className="ds-header-tabs-slot">
              {tabs}
            </Col>
          </Row>
        ) : null}
      </Container>
    </header>
  );
}

Header.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.bool]),
  primaryAction: PropTypes.node,
  search: PropTypes.node,
  viewSwitcher: PropTypes.node,
  tabs: PropTypes.node,
  className: PropTypes.string,
  showBorderBottom: PropTypes.bool,
};

/**
 * Visual shell for search (max 500px, 12px radius) — wrap `InputGroup` / custom field.
 */
export function HeaderSearchShell({ children, className = '', ...props }) {
  return (
    <div className={cx('ds-header-search-shell', className)} {...props}>
      {children}
    </div>
  );
}

HeaderSearchShell.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

/**
 * List / Calendar / Board style segmented control from Figma.
 */
export function HeaderViewSwitcher({ options = [], value, onChange, className = '', ...rest }) {
  return (
    <FlexRow align="center" inline className={cx('ds-header-view-switcher', className)} {...rest}>
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <Button
            key={String(opt.value)}
            variant="white"
            size="sm"
            icon={opt.icon}
            iconPosition="left"
            className={cx('ds-header-view-switcher-option', active && 'is-active')}
            aria-pressed={active}
            onClick={() => onChange?.(opt.value)}
          >
            {opt.label}
          </Button>
        );
      })}
    </FlexRow>
  );
}

HeaderViewSwitcher.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.node.isRequired,
      icon: PropTypes.node,
    })
  ),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  className: PropTypes.string,
};

const HeaderCompound = Header;
HeaderCompound.SearchShell = HeaderSearchShell;
HeaderCompound.ViewSwitcher = HeaderViewSwitcher;

export default HeaderCompound;
